import { AfterViewInit, Component, ElementRef, Inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { idle, IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import { merge } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map as rxMap, tap } from 'rxjs/operators';
import { ApiService } from 'Src/ng2/shared/services/api-service/api-service';
import { Auth } from './../../shared/auth/auth.service';
import { BrowserUpgradeStatus } from './../../shared/constants/browser-versions-supported.constant';
import { PortalConfig } from './../../shared/services/portal-config';
import { UserAgentService } from './../../shared/services/user-agent.service.ts/user-agent.service';
import { LocalStorageService } from './../../shared/services/web-storage/local-storage/local-storage.service';

interface IBrowserMsgConfig {
  text: string | null;
  centerText: boolean;
  showDownloadChromeBtn: boolean;
  showFlagIcon: boolean;
}

@Component({
  selector: 'login',
  template: require('./login.component.html'),
  styles: [require('./login.component.scss')],
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('browserMessage', { static: false }) browserMsgEl: ElementRef;
  private publicConfig: PortalConfig['publicConfig'];
  public browserMessageConfig: IBrowserMsgConfig = {
    text: null,
    centerText: false,
    showDownloadChromeBtn: false,
    showFlagIcon: false,
  };
  public errorMessage: string;
  public hasCaresLoginBtn: boolean = false;
  public maintenanceModeOn$: Observable<boolean>;
  readonly DOWNLOAD_CHROME_URL = 'https://www.google.com/chrome/';

  constructor(
    @Inject('$state') private $state: IStateService,
    @Inject('$window') private $window: IWindowService,
    @Inject('Title') private title: idle.ITitleService,
    private apiService: ApiService,
    private auth: Auth,
    private localStorageService: LocalStorageService,
    private portalConfig: PortalConfig,
    private renderer: Renderer2,
    private userAgentService: UserAgentService,
  ) {
    this.publicConfig = this.portalConfig.publicConfig;
  }

  ngOnInit(): void {
    // check to see if api is in maintenance mode (JE)
    this.maintenanceModeOn$ = this.getMaintenanceMode().pipe(
      tap((modeOn: boolean) => {
        if (!modeOn) {
          this.setBrowserMessageConfig();
        }
      }),
    );

    // Restore tab title (CM);
    this.title.restore();

    // Triggers displaying of error message in login box.
    // We wait one digest cycle to apply error message (CM).
    this.errorMessage = this.getErrorMessage();

    // TEMP - only display cares login button when NOT on prod
    // remove once cares login flow is set up (jchu)
    this.hasCaresLoginBtn = !['prod'].includes(this.publicConfig.DISPLAY_ENV_PATH);
  }

  ngAfterViewInit(): void {
    if (this.browserMsgEl) {
      this.renderer.setProperty(this.browserMsgEl.nativeElement, 'innerHTML', this.browserMessageConfig.text);
    }
  }

  /**
   * Kicks off auth0 google authentication
   */
  loginWithGoogle(): void {
    this.auth.loginWithGoogle();
  }

  /**
   * Kicks off auth0 google authentication
   */
  loginWithNycDoe(): void {
    this.auth.loginWithNycDoe();
  }

  /**
   * Kicks off auth0 cares authentication
   */
  loginWithCares(): void {
    this.auth.loginWithCares();
  }

  // Freshdesk Button
  reportIssues(): void {
    this.$window.FreshWidget.show();
  }

  goToPrivacyAndTerms(): void {
    const url = this.$state.href('privacy-and-terms');
    this.$window.open(url, '_blank');
  }

  private getMaintenanceMode(): Observable<boolean> {
    const NV_ENV = this.getNvEnv();

    // only ping API if on an env with a DISPLAY_NV_PATH property [dev | qa | staging | prod] (JE)
    if (NV_ENV) {
      return this.apiService.getApiStatus().pipe(
        catchError(_ => of(new Error('api is down'))),
        rxMap((res: object | Error) => (res instanceof Error ? true : false)),
      );
    } else {
      // for env [build | local] (jchu)
      return of(false);
    }
  }

  private setBrowserMessageConfig(): void {
    const { name, minVersionReq, status } = this.userAgentService.getBrowserSupportStatus();
    const config = this.getBrowserMessageConfig(status, { name, minVersionReq });
    this.browserMessageConfig = merge(this.browserMessageConfig, config);
  }

  private getBrowserMessageConfig(
    status: string,
    opts: { name: string; minVersionReq: number },
  ): Partial<IBrowserMsgConfig> {
    const downloadChromeHtml = `<b><a href="${this.DOWNLOAD_CHROME_URL}" target="_blank">Chrome</a></b>`;
    let config: Partial<IBrowserMsgConfig>;
    switch (status) {
      case BrowserUpgradeStatus.FullButNotOptimized: {
        const text =
          'This browser is not fully supported. ' + `Please consider using the latest version of ${downloadChromeHtml}`;
        const showFlagIcon = true;
        config = { text, showFlagIcon };
        break;
      }
      case BrowserUpgradeStatus.MustUpdateOrSwitch: {
        const { name, minVersionReq } = opts;
        const minVersionText = minVersionReq ? ` to version <b>${minVersionReq}</b> or above ` : ' ';
        const nameText = name ? `${name}` : '';
        const text =
          'This browser is outdated. ' +
          `Please update <b>${nameText}</b>${minVersionText}` +
          `or use the latest version of ${downloadChromeHtml}.`;
        const showFlagIcon = true;
        const showDownloadChromeBtn = true;
        config = { text, showFlagIcon, showDownloadChromeBtn };
        break;
      }
      case BrowserUpgradeStatus.MustSwitch: {
        const text =
          'This browser is outdated and no longer supported. ' +
          `Please use the latest version of ${downloadChromeHtml}.`;
        const showFlagIcon = true;
        const showDownloadChromeBtn = true;
        config = { text, showFlagIcon, showDownloadChromeBtn };
        break;
      }
      default: {
        const text = '';
        const centerText = true;
        config = { text, centerText };
        break;
      }
    }
    return config;
  }

  private getNvEnv(): string {
    const NV_ENV = this.publicConfig.DISPLAY_ENV_PATH;
    return NV_ENV;
  }

  /**
   * Checks for `errorMessage` in local storage (CM).
   */
  private getErrorMessage(): string {
    const errorMessageKey = 'errorMessage';
    const errorMessage = this.localStorageService.getItem(errorMessageKey);
    this.localStorageService.removeItem('errorMessage');
    return errorMessage;
  }
}
